import React, { ReactElement, useContext } from 'react';
import { Select as AntSelect } from 'antd';
import { colorsPalette } from '../../styles/colors-palette';
import styled from 'styled-components';
import { ValidationContext } from './Validation';
import { Skeleton } from './Skeleton';

//TODO подумать как обойти проверку в ant, если использовать через "свой" компонент ругается, что Select ожидает Select.Options
const { Option } = AntSelect;

interface CustomStyle {
  width?: CSSSize;
  fontSize?: CSSSize;
  borderRadius?: CSSSize;
  borderColor?: Color;
  placeholderColor?: Color;
}

type SelectValueType = string | number | null | undefined;
type SelectOption<ValueType extends SelectValueType> = {
  label: string;
  value: ValueType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
};
type SelectGroup<ValueType extends SelectValueType> = {
  label: string;
  options: SelectOption<ValueType>[];
};
type SelectSizeType = 'large' | 'middle' | 'small';

type SelectEntry<ValueType extends SelectValueType> =
  | SelectOption<ValueType>
  | SelectGroup<ValueType>;

interface SelectProps<ValueType extends SelectValueType> {
  value: ValueType | undefined;
  placeholder?: string;
  children?: React.ReactNode;
  showSearch?: boolean;
  optionFilterProp?: string;
  onChange: (value: ValueType, option: SelectOption<ValueType>) => void;
  onSearch?: (values: string) => void;
  searchValue?: string;
  options?: (SelectOption<ValueType> | SelectGroup<ValueType>)[];
  disabled?: boolean;
  size?: SelectSizeType;
  filter?: (
    input: string,
    option: SelectOption<ValueType> | SelectGroup<ValueType> | undefined,
  ) => boolean;
  onPageScrolled?: () => void;
  className?: string;
  loading?: boolean;
  customStyle?: CustomStyle;
  notFoundContent?: React.ReactNode;
  onDropdownVisibleChange?: (open: boolean) => void;
  popupRender?: React.ReactNode;
  testId?: string;
  optionRender?: (option: SelectEntry<ValueType>) => React.ReactNode;
  labelRender?: (props: { label: React.ReactNode; value: SelectValueType }) => React.ReactNode;
}

function Select<ValueType extends SelectValueType>({
  value,
  placeholder,
  showSearch,
  onSearch,
  searchValue,
  options,
  children,
  onChange,
  disabled,
  filter,
  optionFilterProp = 'label',
  onPageScrolled,
  className,
  size,
  loading,
  customStyle,
  notFoundContent,
  onDropdownVisibleChange,
  popupRender,
  testId,
  optionRender,
  labelRender,
}: SelectProps<ValueType>): ReactElement {
  const { validateStatus } = useContext(ValidationContext);

  if (loading) {
    return <StyledSkeleton width={customStyle?.width} className={className} />;
  }

  // TODO maybe need to inc size(32) every scroll turn
  const antPopupScroll = (event: React.UIEvent<HTMLDivElement, UIEvent>) => {
    const target: HTMLDivElement = event.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight - 32) {
      onPageScrolled && onPageScrolled();
    }
  };

  return (
    <StyledSelect
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      customstyles={customStyle}
      value={value}
      placeholder={placeholder}
      showSearch={showSearch}
      onSearch={onSearch}
      optionFilterProp={optionFilterProp}
      notFoundContent={notFoundContent}
      onDropdownVisibleChange={onDropdownVisibleChange}
      status={validateStatus}
      onChange={(value, option) => onChange(value, option as SelectOption<ValueType>)}
      options={options}
      disabled={disabled}
      dropdownRender={(menu: React.ReactNode) => (
        <>
          {menu}
          {popupRender && popupRender}
        </>
      )}
      searchValue={searchValue}
      filterOption={filter}
      className={className}
      onPopupScroll={antPopupScroll}
      size={size ? size : 'large'}
      data-testid={testId}
      optionRender={optionRender as (option: unknown) => React.ReactNode}
      labelRender={labelRender as (props: unknown) => React.ReactNode}
    >
      {children}
    </StyledSelect>
  );
}

const StyledSelect = styled(AntSelect)<{ customstyles?: CustomStyle }>`
  &&.ant-select .ant-select-selector {
    font-size: ${props => props.customstyles?.fontSize};
    border-radius: ${props => props.customstyles?.borderRadius};
    border-color: ${props => props.customstyles?.borderColor};
  }

  &&.ant-select .ant-select-selection-placeholder {
    color: ${props => props.customstyles?.placeholderColor};
  }

  &.ant-select {
    width: ${props => props.customstyles?.width || '100%'};

    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          &:hover {
            .ant-select-selector {
              border-color: ${colorsPalette.dustyBlue500};
            }
          }
        }
      }
    }
  }

  &.ant-select-focused {
    &:not(.ant-select-disabled) {
      &:not(.ant-select-customize-input) {
        &:not(.ant-pagination-size-changer) {
          .ant-select-selector {
            border-color: ${colorsPalette.dustyBlue500};
          }
        }
      }
    }
  }
` as typeof AntSelect;

const StyledSkeleton = styled(Skeleton)`
  &.ant-skeleton.ant-skeleton-element .ant-skeleton-input {
    height: 40px;
    vertical-align: middle;
    border-radius: 8px;
  }
`;

export {
  Select,
  type SelectOption,
  type SelectGroup,
  type SelectEntry,
  type SelectValueType,
  Option as SelectOptionView,
};
